import { ArrowRight, Award, Building } from "lucide-react";
import { Button } from "@/components/ui/button";

const ZYPPER_IMG =
  "https://images.unsplash.com/photo-1607860108855-64acf2078ed9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxjYXIlMjB3YXNoJTIwcHJvZmVzc2lvbmFsJTIwdW5pZm9ybXxlbnwwfHx8fDE3ODEyNjMwMjB8MA&ixlib=rb-4.1.0&q=85";

const scrollTo = (id, persona) => {
  const el = document.querySelector(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth" });
  }
  // Set persona in URL hash
  if (persona) {
    window.dispatchEvent(
      new CustomEvent("zyp-set-persona", { detail: persona })
    );
  }
};

export default function Marketplace() {
  return (
    <section
      id="partners"
      data-testid="marketplace-partners"
      className="relative py-24 md:py-32 px-6 md:px-12 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-[0.22em] text-[#FF5A00] mb-4">
            Three-sided marketplace
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight max-w-3xl mx-auto">
            Built for customers, Zyppers <br />
            and societies — equally.
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Become a Zypper */}
          <div
            data-testid="card-become-zypper"
            className="relative rounded-3xl overflow-hidden border border-white/10 group min-h-[480px]"
          >
            <img
              src={ZYPPER_IMG}
              alt="Professional Zypper detailing car"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/70 to-transparent" />
            <div className="relative h-full p-10 md:p-12 flex flex-col justify-end">
              <div className="inline-flex items-center gap-2 mb-4">
                <Award size={16} className="text-[#FF5A00]" />
                <span className="text-xs uppercase tracking-[0.2em] text-zinc-300">
                  For Zyppers
                </span>
              </div>
              <h3 className="font-heading text-3xl md:text-4xl font-medium tracking-tight max-w-md">
                Build a real career in car care.
              </h3>
              <p className="mt-4 text-zinc-300 text-sm md:text-base max-w-md leading-relaxed">
                Predictable shifts in your zone. Free training & certifications.
                Insurance, incentives and a tier system that pays for skill.
              </p>
              <Button
                data-testid="cta-become-zypper"
                onClick={() => scrollTo("#waitlist", "zypper")}
                className="mt-8 w-fit bg-white text-black hover:bg-zinc-200 rounded-full px-7 h-12 font-medium group/btn"
              >
                Become a Zypper
                <ArrowRight
                  size={16}
                  className="ml-1 group-hover/btn:translate-x-1 transition-transform"
                />
              </Button>
            </div>
          </div>

          {/* Partner society */}
          <div
            data-testid="card-partner-society"
            className="relative rounded-3xl overflow-hidden border border-white/10 group min-h-[480px] bg-gradient-to-br from-zinc-900 via-zinc-950 to-black"
          >
            <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-[#FF5A00]/10 blur-3xl" />
            <div className="relative h-full p-10 md:p-12 flex flex-col justify-end">
              <div className="inline-flex items-center gap-2 mb-4">
                <Building size={16} className="text-[#FF5A00]" />
                <span className="text-xs uppercase tracking-[0.2em] text-zinc-300">
                  For Societies & RWAs
                </span>
              </div>
              <h3 className="font-heading text-3xl md:text-4xl font-medium tracking-tight max-w-md">
                Turn your basement into a revenue stream.
              </h3>
              <p className="mt-4 text-zinc-400 text-sm md:text-base max-w-md leading-relaxed">
                Get a revenue share on every wash. Verified, uniformed Zyppers.
                Compliance-friendly waterless tech. Happy residents.
              </p>

              <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
                {[
                  { k: "0L", v: "Water used" },
                  { k: "100%", v: "Photo verified" },
                  { k: "Verified", v: "Workforce" },
                ].map((s) => (
                  <div
                    key={s.v}
                    className="rounded-xl border border-white/10 bg-white/[0.02] p-3"
                  >
                    <div className="font-heading text-lg font-medium">
                      {s.k}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      {s.v}
                    </div>
                  </div>
                ))}
              </div>

              <Button
                data-testid="cta-partner-society"
                onClick={() => scrollTo("#waitlist", "society")}
                className="mt-8 w-fit bg-[#FF5A00] hover:bg-[#FF7A33] text-white rounded-full px-7 h-12 font-medium group/btn"
              >
                Partner with Zyp
                <ArrowRight
                  size={16}
                  className="ml-1 group-hover/btn:translate-x-1 transition-transform"
                />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
